import io from "socket.io-client";
import { getSocketConnectionConfig } from "./backendUrl";

const createNoopSocket = () => ({
  connected: false,
  on: () => {},
  off: () => {},
  emit: (_event, _data, callback) => {
    if (typeof callback === "function") {
      callback();
    }
  },
  disconnect: () => {}
});

class SocketWorker {
  constructor(companyId, userId) {
    if (!companyId || !userId) {
      throw new Error("SocketWorker requires companyId and userId");
    }

    if (
      SocketWorker.instance &&
      SocketWorker.instance.companyId === companyId &&
      SocketWorker.instance.userId === userId
    ) {
      return SocketWorker.instance;
    }

    if (SocketWorker.instance) {
      SocketWorker.instance.disconnect();
    }

    this.companyId = companyId;
    this.userId = userId;
    this.socket = null;
    this.eventListeners = {};

    this.configureSocket();

    SocketWorker.instance = this;

    return this;
  }

  configureSocket() {
    const token = localStorage.getItem("token");

    if (!token || !this.companyId || !this.userId) {
      return;
    }

    const userIdString = String(this.userId);

    const { socketUrl, socketPath } = getSocketConnectionConfig(`workspace-${this.companyId}`);

    this.socket = io(socketUrl, {
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 2,
      reconnectionDelayMax: 5000,
      transports: ["websocket", "polling"],
      pingTimeout: 20000,
      pingInterval: 25000,
      path: socketPath,
      query: {
        userId: userIdString,
        token
      }
    });

    this.socket.on("connect_error", error => {
      if (error.message === "Token inv\u00e1lido" || error.message === "Token ausente") {
        this.disconnect();
      }
    });

    this.socket.on("disconnect", () => {
      this.reconnectAfterDelay();
    });
  }

  on(event, callback) {
    this.connect();

    if (!this.socket) {
      return;
    }

    this.socket.on(event, callback);

    if (!this.eventListeners[event]) {
      this.eventListeners[event] = [];
    }

    this.eventListeners[event].push(callback);
  }

  emit(event, data, callback) {
    this.connect();

    if (!this.socket) {
      return;
    }

    if (callback) {
      this.socket.emit(event, data, callback);
    } else {
      this.socket.emit(event, data);
    }
  }

  off(event, callback) {
    this.connect();

    if (!this.eventListeners[event]) {
      return;
    }

    if (callback) {
      this.socket.off(event, callback);
      this.eventListeners[event] = this.eventListeners[event].filter(cb => cb !== callback);
      return;
    }

    this.eventListeners[event].forEach(cb => this.socket.off(event, cb));
    delete this.eventListeners[event];
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }

    SocketWorker.instance = null;
  }

  reconnectAfterDelay() {
    setTimeout(() => {
      if (!this.socket) {
        this.configureSocket();
        return;
      }

      if (typeof this.socket.connect === "function" && !this.socket.connected) {
        this.socket.connect();
      } else if (!this.socket.connected) {
        this.connect();
      }
    }, 1000);
  }

  connect() {
    if (!this.socket) {
      this.configureSocket();
    }
  }

  forceReconnect() {}
}

const instance = (companyId, userId) => new SocketWorker(companyId, userId);

export default instance;
